import React, { useEffect, useLayoutEffect, useState } from "react";
import style from "./SkinHealthHome.module.scss";
import { HomePageSkeleton } from "./HomePageSkeleton";
import { useDispatch, useSelector } from "react-redux";
import searchIcon from "../../../static/images/search-icon.svg";
import { getLayoutDetailsAsync, sendNonPaidRequestDemoEmail } from "../../../app/homeSlice";
import { InsightsStrategies } from "./InsightsStrategies";
import { LayoutCards } from "./LayoutCards";
import { useDebounce } from "../../../utils";
import { SHProducts } from "./Products/SHProducts";
import { Formulary } from "./Formulary/Formulary";
import { useNavigate } from "react-router-dom";
import Banner from "./Banner";
import { ClinicalResources } from "./ClinicalResources/ClinicalResources";
import { ProductSelector } from "./ProductSelector/ProductSelector";
import { toast } from "react-toastify";
import { useLoading } from "../../../hooks/useLoading";
import { LOADING_KEYS } from "../../../constants/loadingKeys";
import constants from "../../../constants/constants.json";

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 400;

export const SkinHealthHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const nonPaidLoading = useLoading(LOADING_KEYS.NON_PAID_REQUEST_DEMO);

  const userDetails = useSelector((state) => state.user.userDetails);
  const layoutDetails = useSelector((state) => state.home.layoutDetails);

  const isLoading = !layoutDetails || !layoutDetails.banners || !layoutDetails.cards || !layoutDetails.listViewMenus;

  // shimmer state
  const [isPageReady, setIsPageReady] = useState(!isLoading);

  const [selectedChips, setSelectedChips] = useState([]);
  const [filteredListViewMenus, setFiltredListViewMenus] = useState([]);
  const [loadedImages, setLoadedImages] = useState({});
  const [searchProduct, setSearchProduct] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [formularyModal, setFormularyModal] = useState(false);
  const [clinicalResourcesModal, setClinicalResourcesModal] = useState(false);
  const [productSelectorModal, setProductSelectorModal] = useState(false);

  const cardActions = {
    Formulary: () => setFormularyModal(true),
    "Risk Assessment": () => navigate("/riskassessment"),
    "Clinical Resources": () => setClinicalResourcesModal(true),
    "Product Selector": () => setProductSelectorModal(true),
    "Wound Management": () => navigate("/patients"),
  };

  const debouncedSearch = useDebounce(searchProduct, SEARCH_DEBOUNCE_MS);

  const toggleChip = (chip) => {
    setSelectedChips((prev) => (prev.includes(chip) ? prev.filter((c) => c !== chip) : [...prev, chip]));
  };

  useEffect(() => {
    dispatch(getLayoutDetailsAsync());
  }, [dispatch]);

  useEffect(() => {
    if (!isLoading) setIsPageReady(true);
  }, [isLoading]);

  useLayoutEffect(() => {
    if (!layoutDetails?.listViewMenus) return;

    if (selectedChips.length === 0) {
      setFiltredListViewMenus(layoutDetails.listViewMenus);
      return;
    }

    const allowedMenuIds = layoutDetails?.filters?.filter((filter) => selectedChips.includes(filter.id))?.flatMap((filter) => filter.listViewMenus);
    const uniqueMenuIds = [...new Set(allowedMenuIds)];
    const filtered = layoutDetails?.listViewMenus.filter((menu) => uniqueMenuIds.includes(menu.id));

    setFiltredListViewMenus(filtered);
  }, [selectedChips, layoutDetails]);

  const handleImageLoad = (id) => {
    setLoadedImages((prev) => ({ ...prev, [id]: true }));
  };

  useEffect(() => {
    if (debouncedSearch.trim().length >= MIN_SEARCH_LENGTH) {
      setModalOpen(true);
      setSearchProduct("");
    }
  }, [debouncedSearch]);

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleCardClick = (name) => {
    cardActions[name]?.();
  };

  const handleLaunch = async () => {
    if (userDetails?.ne1Wms === 1) {
      navigate("/patients");
    } else {
      const result = await dispatch(sendNonPaidRequestDemoEmail());
      if(result) {
        toast.success(constants.home.nonPaidDemo, { containerId: "warning" });
      }
    }
  };

  if (!isPageReady) return <HomePageSkeleton />;

  return (
    <div className={`${style.home_container} ${style.page_fade_in}`}>
      <div className={`${style.header_section} text-center`}>
        <h2>Hi {userDetails.firstName}</h2>
        <h3>
          Welcome to Medline Ski<span className="font-weight-bold">n Health</span>
        </h3>

        <div className={`${style.search_bar} mx-auto`}>
          <img className={style.icon} src={searchIcon} alt="search icon" />
          <input type="text" placeholder="Find skin health products" value={searchProduct} onChange={(e) => setSearchProduct(e?.target?.value)} />
        </div>
      </div>

      <Banner onClick={handleLaunch} ne1Wms={userDetails?.ne1Wms} loading={nonPaidLoading} />

      <LayoutCards loading={isLoading} layoutDetails={layoutDetails} images={loadedImages} onLoad={handleImageLoad} onCard={handleCardClick} />

      <InsightsStrategies loading={isLoading} cards={filteredListViewMenus} layoutDetails={layoutDetails} chips={selectedChips} toggle={toggleChip} />

      {modalOpen && <SHProducts modalOpen={modalOpen} modalClose={handleModalClose} initialSearchTerm={debouncedSearch} />}

      {formularyModal && <Formulary modalOpen={formularyModal} onClose={() => setFormularyModal(false)} />}

      {clinicalResourcesModal && <ClinicalResources modalOpen={clinicalResourcesModal} onClose={() => setClinicalResourcesModal(false)} />}

      {productSelectorModal && <ProductSelector modalOpen={productSelectorModal} onClose={() => setProductSelectorModal(false)} />}
    </div>
  );
};
